import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export * from "./dateformat.js";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const isActivePath = ($page, href) => {
  return $page.url.pathname === href || $page.url.pathname === href + "/";
};
export const isExternal = (link) => /^https?:\/\//.test(link);

export function formatPublishedAt(date, local = "en-US") {
  return new Date(date).toLocaleDateString(local, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC", // don't convert to client's local timezone
  });
}

export const slugify = (text, args) => {
  const options = Object.assign(
    {
      prefix: undefined,
      suffix: undefined,
    },
    args,
  );

  try {
    let result = text
      .toString() // Cast to string (optional)
      .normalize("NFKD") // The normalize() using NFKD method returns the Unicode Normalization Form of a given string.
      .toLowerCase() // Convert the string to lowercase letters
      .trim() // Remove whitespace from both sides of a string (optional)
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w-]+/g, "") // Remove all non-word chars
      .replace(/--+/g, "-"); // Replace multiple - with single -
    if (options.prefix) result = String(options.prefix).concat("-", result);
    if (options.suffix) result = result.concat("-", options.suffix);

    return result;
  } catch (error) {
    console.error(error);
    return text;
  }
};

export const turnstileErrorCodes = {
  "missing-input-secret": "The secret parameter was not passed.",
  "invalid-input-secret": "The secret parameter was invalid or did not exist.",
  "missing-input-response": "The response parameter (token) was not passed.",
  "invalid-input-response":
    "The response parameter (token) is invalid or has expired. Most of the time, this means a fake token has been used. If the error persists, contact customer support.",
  "bad-request": "The request was rejected because it was malformed.",
  "timeout-or-duplicate":
    "The response parameter (token) has already been validated before. This means that the token was issued five minutes ago and is no longer valid, or it was already redeemed.",
  "internal-error": "An internal error happened while validating the response. The request can be retried.",
};

// export function extractFrontmatter(src) {
//   const m = src.match(/^---\r?\n([\s\S]+?)\r?\n---/);
//   if (!m) return {};

//   const lines = m[1].split(/\r?\n/);
//   const out = {};
//   for (const line of lines) {
//     const [k, ...rest] = line.split(":").map((s) => s.trim());
//     if (!k) continue;
//     let v = rest.join(":"); // "description: foo: bar"
//     if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
//       v = v.slice(1, -1);
//     }
//     out[k] = v;
//   }
//   return out;
// }

export function extractFrontmatter(src) {
  const m = src.match(/^---\r?\n([\s\S]+?)\r?\n---/);
  if (!m) return {};

  const yamlContent = m[1];
  const lines = yamlContent.split(/\r?\n/);
  const result = {};
  const stack = [{ obj: result, indent: -1 }];
  let currentKey = null;
  let arrayIndent = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim() || line.trim().startsWith("#")) continue;

    const indent = line.search(/\S/);
    const trimmed = line.trim();

    // Array item
    if (trimmed.startsWith("- ")) {
      const value = trimmed.slice(2).trim();

      if (arrayIndent === -1) {
        arrayIndent = indent;
      }

      // Pop stack to correct level
      while (stack.length > 1 && stack[stack.length - 1].indent >= arrayIndent) {
        stack.pop();
      }

      const parent = stack[stack.length - 1].obj;
      if (currentKey && !Array.isArray(parent[currentKey])) {
        parent[currentKey] = [];
      }

      const parsedValue = parseValue(value);

      if (currentKey) {
        parent[currentKey].push(parsedValue);
      }
      continue;
    }

    // Key-value pair
    if (trimmed.includes(":")) {
      const colonIndex = trimmed.indexOf(":");
      const key = trimmed.slice(0, colonIndex).trim();
      let value = trimmed.slice(colonIndex + 1).trim();

      // Pop stack to correct indentation level
      while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
        stack.pop();
      }

      const parent = stack[stack.length - 1].obj;

      // Empty value - might be nested object or array
      if (!value) {
        // Check next line to determine if it's an array or object
        const nextLine = lines[i + 1];
        if (nextLine) {
          const nextTrimmed = nextLine.trim();
          if (nextTrimmed.startsWith("- ")) {
            parent[key] = [];
            currentKey = key;
            arrayIndent = -1;
          } else {
            parent[key] = {};
            stack.push({ obj: parent[key], indent });
          }
        }
      } else {
        parent[key] = parseValue(value);
        currentKey = key;
        arrayIndent = -1;
      }
    }
  }

  return result;
}

function parseValue(value) {
  if (!value) return "";

  // Remove quotes
  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1);
  }

  // Boolean
  if (value === "true") return true;
  if (value === "false") return false;

  // Null
  if (value === "null" || value === "~") return null;

  // Number
  if (/^-?\d+(\.\d+)?$/.test(value)) {
    return parseFloat(value);
  }

  return value;
}

/**
 * Convert short locale code (e.g. 'tr', 'en', 'fr')
 * to full Open Graph compatible format (e.g. 'tr_TR', 'en_US', 'fr_FR')
 */
export function toOgLocale(lang) {
  const map = {
    tr: "tr_TR",
    en: "en_US",
    fr: "fr_FR",
    de: "de_DE",
    es: "es_ES",
    it: "it_IT",
    nl: "nl_NL",
    ru: "ru_RU",
    ja: "ja_JP",
    zh: "zh_CN",
  };

  return map[lang] || `${lang}_${lang.toUpperCase()}`;
}
